import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const APP_NAME = 'Nurture Buddy.ai';

const PLATFORM_PRIVACY = `
This Privacy Policy describes how Nurture Buddy.ai ("we," "our," or "us") collects, uses, and protects information when you use our platform and SMS messaging services.

**Information we collect**
When you sign up for Nurture Buddy.ai, we collect your name, email address, phone number (optional), and business information. When you add clients or leads to the platform, we store their contact information and message history to deliver SMS touchpoints on your behalf. We use cookies and similar technologies to operate our web application.

**How we use your information**
We use your information to provide our services: sending AI-generated SMS messages to your clients, managing conversations, and delivering analytics. We do not sell your personal information or your clients' information to third parties for their marketing purposes.

**Data handling**
Messaging is powered by Twilio and other service providers for delivery. Your data is stored securely and used only for the purposes described above and in accordance with applicable privacy laws, including TCPA and carrier requirements.

**Opt-out**
Recipients of SMS messages can opt out at any time by replying STOP to any message. Reply HELP for help or more information.

**Data retention**
We retain your account data and message history as long as your account is active. You may request deletion of your data by contacting us.

**Contact**
For questions about this privacy policy or your data, please contact us at support@nurturebuddy.ai.

Last updated: ${new Date().toISOString().slice(0, 10)}
`;

const PLATFORM_TERMS = `
These Terms of Service ("Terms") govern your use of Nurture Buddy.ai and our SMS messaging platform.

**Acceptance**
By creating an account or using our services, you agree to these Terms. If you do not agree, do not use our services.

**Service description**
Nurture Buddy.ai provides AI-powered SMS touchpoint automation for real estate professionals. You use our platform to send messages to your clients and leads. You are responsible for obtaining proper consent from recipients before adding them to the platform and for the content of messages sent through your account.

**Consent and compliance**
You represent that you have obtained appropriate consent from message recipients and that your use of our services complies with TCPA, carrier rules (including A2P 10DLC), and applicable laws. Recipients may opt out by replying STOP. Message and data rates may apply.

**Account responsibility**
You are responsible for maintaining the security of your account and for all activity under your account. You must not use our services for spam, harassment, or unlawful purposes.

**Service availability**
We strive for high availability but do not guarantee uninterrupted service. We may modify, suspend, or discontinue features with reasonable notice when possible.

**Limitation of liability**
To the maximum extent permitted by law, Nurture Buddy.ai and its providers are not liable for indirect, incidental, or consequential damages arising from your use of our services.

**Contact**
For questions about these terms, please contact us at support@nurturebuddy.ai.

Last updated: ${new Date().toISOString().slice(0, 10)}
`;

function GlobalPolicyPage() {
  const location = useLocation();
  const pathname = location?.pathname || '';
  const isPrivacy = pathname === '/privacy-policy' || pathname.endsWith('/privacy-policy');
  const isTerms = pathname === '/terms' || pathname.endsWith('/terms');
  const valid = isPrivacy || isTerms;

  if (!valid) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <p className="text-muted-foreground">Page not found.</p>
      </div>
    );
  }

  const title = isPrivacy ? `Privacy Policy – ${APP_NAME}` : `Terms of Service – ${APP_NAME}`;
  const content = isPrivacy ? PLATFORM_PRIVACY : PLATFORM_TERMS;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="min-h-screen bg-background text-foreground">
        <div className="max-w-2xl mx-auto px-4 py-10 sm:py-14">
          <div className="mb-8">
            <Link
              to="/"
              className="text-sm text-muted-foreground hover:text-foreground transition-smooth inline-flex items-center gap-1"
            >
              ← Back to home
            </Link>
          </div>
          <h1 className="text-2xl font-bold mb-6">
            {isPrivacy ? 'Privacy Policy' : 'Terms of Service'}
          </h1>
          <div className="prose prose-sm max-w-none text-foreground/90 whitespace-pre-wrap">
            {content.trim().replace(/\*\*(.+?)\*\*/g, '$1')}
          </div>
          <div className="mt-10 pt-6 border-t" style={{ borderColor: 'rgba(30,45,69,0.6)' }}>
            <Link to="/" className="text-sm text-primary hover:text-primary/80">
              Return to Nurture Buddy.ai
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default GlobalPolicyPage;
